<!-- eslint-disable vue/no-unused-refs -->
<template>
  <div
    :id="idWrap"
    class="tw-flex tw-flex-1 tw-max-w-[519.541px] tw-items-start tw-gap-2.5 tw-border tw-px-5 tw-py-[25px] tw-rounded-md tw-border-solid tw-border-[#eff0f1] tw-relative fileUploaderWrapper"
    data-cy="add-evidence-modal-click-to-upload-btn"
  >
    <div
      v-if="onLoading === API_STATUS_LOADING.LOADING"
      class="tw-flex tw-items-center tw-justify-center tw-w-full fileUploader tw-h-[88px]"
    >
      <uploadLoadingButton />
    </div>
    <div
      v-else-if="
        imgSrc === null ||
        onLoading === API_STATUS_LOADING.ERROR ||
        onLoading === API_STATUS_LOADING.NO_ACTION ||
        isMultiple
      "
      class="tw-cursor-[pointer] tw-flex tw-flex-col tw-items-center tw-gap-2.5 tw-w-full fileUploader"
      :data-cy="`img-detail-image-${type}`"
      @click="clickInput"
      @dragover.prevent
      @drop="handleDrop"
    >
      <span
        class="tw-flex tw-w-[43px] tw-h-[43px] tw-justify-center tw-items-center tw-pl-[7.984px] tw-pr-[7.987px] tw-pt-[9.828px] tw-pb-[9.962px] tw-rounded-[307.143px] uploadIconWrapper"
      >
        <img
          src="/img/upload_icon.svg"
          alt="upload"
          class="tw-invert-[82%] tw-sepia-[0%] tw-saturate-[1%] tw-hue-rotate-[232deg] tw-brightness-[91%] tw-contrast-[91%] tw-w-[27.029px] tw-h-[23.21px] uploadIcon"
        />
      </span>
      <span
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[2px] info"
      >
        <span
          :class="`tw-text-[#878787] tw-text-center tw-text-[${fontSizeTitle}px] tw-not-italic tw-font-normal tw-leading-[135%] text`"
          ><span
            data-cy="file-uploader-button"
            class="tw-text-[#387dff] tw-underline clickUploadText"
            >{{ getUiText("app-component-file-upload-select-title-a-1") }}</span
          >
          {{ getUiText("app-component-file-upload-select-title-b-1") }}</span
        >
        <input
          :id="idInput"
          ref="fileInput"
          class="tw-invisible tw-w-0 tw-h-0"
          type="file"
          @change="(e: any) => handleFileSelect(e.target.files[0])"
        />
        <span
          class="tw-text-[#b7b7b7] tw-text-center tw-text-[11px] tw-not-italic tw-font-normal tw-leading-[135%] formats"
          >{{ guidance }}</span
        >
      </span>
    </div>

    <div
      v-else-if="onLoading === API_STATUS_LOADING.SUCCESS && !isMultiple"
      class="tw-flex tw-w-full tw-justify-center preview"
    >
      <img
        class="tw-h-[88px]"
        :class="useRectangleStencil ? '' : 'tw-w-[88px] tw-rounded-[50%]'"
        :src="croppedImage"
        alt="image"
      />
      <span
        class="tw-cursor-pointer tw-absolute tw-right-[10px] tw-top-[10px] tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-gap-[7.143px] tw-bg-[#878787] tw-px-[5.714px] tw-py-[1.429px] tw-rounded-[50%]"
        @click="reset"
      >
        <img
          class="tw-w-[8.571px] tw-h-[8.572px]"
          src="/img/close_icon_white.svg"
          :data-cy="`img-detail-close-icon-${type}`"
          alt="close"
        />
      </span>
    </div>
    <ImageCropper
      :visible="isImageCropperVisible"
      :src="imgSrcString"
      :src-type="imgSrc?.type"
      :title="cropModalTitle"
      :formats="cropFormats"
      :recommendations="
        getUiText('app-settings-organisation-profile-avatar-cropper-formats-2')
      "
      :is-small-screen="isSmallScreen"
      :rounded="rounded"
      :use-rectangle-stencil="useRectangleStencil"
      @cropped-img="(blob) => emitFile(blob)"
      @close="emitFile(null)"
    />
  </div>
</template>

<script lang="ts">
import store from "@/store";
import utilities, { getComponentUiText, handleSvg } from "@/utils/utilities";
import { Ref, ref } from "vue";
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import ImageCropper from "./imageCropper.vue";
import {
  ACCEPTABLE_AVATAR_FILE_TYPE,
  API_STATUS_LOADING,
} from "@/utils/constants";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { toBase64 } from "@/utils/cipher";
import { fileSizeFormat } from "@/utils/dateTimeUtils";
import uploadLoadingButton from "@/components/common/buttons/uploadLoadingButton.vue";

const MAX_AVT_SIZE = process.env.VUE_APP_MAX_AVT_SIZE || 1048576;
const MAX_LOGO_SIZE = process.env.VUE_APP_MAX_LOGO_SIZE || 2097152;
const MAX_EVID_SIZE = process.env.VUE_APP_MAX_EVID_SIZE || 52428800;

@Component({
  emits: ["update:file"],
  components: {
    ImageCropper,
    uploadLoadingButton,
  },
})
export default class FileUploader extends Vue {
  @Prop({ required: true, default: null }) public file!: any;
  @Prop({ required: true, default: null }) public guidance;
  @Prop({ required: false, default: false }) public crop!: boolean;
  @Prop({ required: false, default: true }) public cropSvg = true;
  @Prop({ required: false, default: false }) public isProfile!: boolean;
  @Prop({ required: false, default: false }) public isEvidence!: boolean;
  @Prop({ required: false, default: false }) public onCropSubmit!: boolean;
  @Prop({ required: false, default: false }) public onLoading!: string;
  @Prop({ required: false, default: false }) public cropModalTitle!: string;
  @Prop({ required: false, default: false }) public cropFormats!: string;
  @Prop({ required: false, default: true }) public rounded!: boolean;
  @Prop({ required: false, default: true }) fontSizeTitle = 14;
  @Prop({ required: false, default: null }) public type!: string;
  @Prop({ required: false, default: false }) public idInput!: string;
  @Prop({ required: false, default: false }) public idWrap!: string;
  @Prop({ required: false, default: false }) public isMultiple!: boolean;
  @Prop({ required: false, default: false })
  public useRectangleStencil!: boolean;

  public imgSrc: any = ref(null);
  public imgSrcString: any = ref("");
  public isImageCropperVisible: boolean | Ref = ref(false);
  public croppedImage: any = ref(null);
  public isSmallScreen: boolean | Ref = ref(false);
  public API_STATUS_LOADING = API_STATUS_LOADING;
  // Type of File.
  public fileTypeImage = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/svg+xml",
  ];
  public fileTypeDocument = ["application/pdf", "application/msword", ""];

  get languageSelected() {
    return store.state.LanguagePicker.languageSelected;
  }
  get defaultUiText() {
    return store.state.LanguagePicker.defaultUiText;
  }
  get languageSelectedText() {
    return store.state.LanguagePicker.languageSelectedText;
  }

  @Watch("isImageCropperVisible", { immediate: true })
  public onIsImageCropperVisibleChange() {
    EventEmitter.emit(
      EmitterEvents.APP_LAYOUT_SHOW_COLLAPSABLE_MENU,
      this.isImageCropperVisible,
    );
  }

  @Watch("imgSrc", { immediate: true })
  public onImgSrcChange() {
    this.croppedImage = this.imgSrc
      ? typeof this.imgSrc === "string"
        ? this.imgSrc
        : URL.createObjectURL(this.imgSrc)
      : null;
  }

  @Watch("file", { immediate: true })
  public onFileChange() {
    if (!this.file || !(this.file instanceof File)) return;

    this.imgSrc = this.file;
    this.croppedImage = URL.createObjectURL(this.file);
  }

  @Watch("onLoading", { immediate: true })
  public onLoadingChange() {
    if (this.onLoading === API_STATUS_LOADING.ERROR) {
      this.imgSrc = null;
      this.croppedImage = null;
    }
  }

  getUiText(id: string, isArray = false) {
    return utilities.getUiText(
      id,
      this.languageSelectedText,
      this.defaultUiText,
      isArray,
    );
  }

  public handleDrop(event) {
    event.preventDefault();
    this.handleFileSelect(event.dataTransfer.files[0]);
  }

  public async handleFileSelect(selectFile: any = null) {
    if (!selectFile) return;

    if (
      !ACCEPTABLE_AVATAR_FILE_TYPE.includes(selectFile.type) &&
      this.isProfile
    ) {
      this.showError(
        this.getUiText(
          "app-component-file-updload-toast-message-invalid-file-format-error",
        ),
      );
      return;
    }

    if (!this.isFileSizeValid(selectFile)) return;

    this.imgSrc = selectFile;

    if (!this.cropSvg && this.imgSrc?.type === "image/svg+xml") {
      this.$emit("update:file", selectFile);
      return;
    }

    if (this.crop) {
      if (this.imgSrc?.type === "image/svg+xml") {
        this.imgSrc = await handleSvg(this.imgSrc);
      }

      this.imgSrcString = await toBase64(this.imgSrc);
      this.isImageCropperVisible = true;
    } else this.$emit("update:file", selectFile);

    // this.imgSrc = null;
    // if (!this.crop) this.$emit("update:file", selectFile);
    // this.$emit("onImageRemove");
  }

  isFileSizeValid(file: any) {
    const maxSize = this.calculateMaxSize();

    if (file.size > maxSize) {
      this.showTextError();
      return false;
    }

    return true;
  }

  calculateMaxSize() {
    if (this.isEvidence) return MAX_EVID_SIZE;
    if (!this.crop) return MAX_LOGO_SIZE;

    return this.useRectangleStencil ? MAX_LOGO_SIZE : MAX_AVT_SIZE;
  }

  showTextError() {
    let text = "";
    const maxSize = this.calculateMaxSize();
    const maxSizeConverted = fileSizeFormat(maxSize);

    if (this.isEvidence)
      text = getComponentUiText(
        "app-component-file-upload-toast-message-invalid-file-size-error-3",
        false,
        { maxSize: maxSizeConverted },
      );

    if (this.useRectangleStencil)
      text = getComponentUiText(
        "app-component-file-upload-toast-message-invalid-file-size-error-2",
        false,
        { maxSize: maxSizeConverted },
      );
    else
      text = getComponentUiText(
        "app-component-file-upload-toast-message-invalid-file-size-error-1",
        false,
        { maxSize: maxSizeConverted },
      );

    this.showError(text);
  }

  // show errror message
  showError(message: string) {
    const emitData: IToastMessage = {
      variant: ToastVariant.ERROR,
      message,
    };
    EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
  }

  clickInput() {
    (this.$refs as any).fileInput.click();
  }

  public async emitFile(canvas: any) {
    this.isImageCropperVisible = false;

    if (!canvas) {
      this.imgSrc = null;
      return this.$emit("update:file", null);
    }

    const blob: any = await new Promise((resolve) =>
      canvas.toBlob(resolve, this.imgSrc.type?.toLowerCase(), 1),
    );

    this.croppedImage = URL.createObjectURL(blob);

    this.$emit(
      "update:file",
      new File([blob], this.imgSrc?.name, { type: blob.type }),
    );

    if (this.onCropSubmit) {
      this.$emit(
        "submit",
        new File([blob], this.imgSrc?.name, { type: blob.type }),
      );
    }
  }

  reset() {
    this.imgSrc = null;
    this.croppedImage = null;
    this.$emit("onImageRemove");
  }

  checkScreenSize() {
    const screenWidth = window.innerWidth;
    if (screenWidth < 600) this.isSmallScreen = true;
  }

  mounted() {
    this.checkScreenSize();
  }
}
</script>

<style scoped>
.fileUploaderWrapper {
  background: rgba(239, 240, 241, 0.5);
}

.uploadIconWrapper {
  background: #eeeff0;
}

.text {
  font-family: Poppins;
}

.formats {
  font-family: Poppins;
}
</style>
